import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Check, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Pricing() {
  const navigate = useNavigate();
  const { user, subscription } = useAuth();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data.plans || [])).catch(() => {});
  }, []);

  const subscribe = async (plan) => {
    if (!user) { navigate("/register"); return; }
    if (!plan.stripe_lookup_key) { navigate("/campaigns"); return; }
    setBusy(plan.key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: plan.stripe_lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed");
      setBusy("");
    }
  };

  const currentPlan = subscription?.plan || "free";

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex px-3 py-1 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30 text-xs font-mono uppercase tracking-widest mb-4">Simple pricing</div>
          <h1 className="font-display text-4xl font-bold">Free to discover. Paid to create.</h1>
          <p className="mt-3 text-zinc-400 max-w-xl mx-auto">
            Explore and save campaigns free forever. Subscribe to unlock the AI clipping tools and monthly credits.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
          {plans.map((p) => {
            const isCurrent = p.key === currentPlan;
            const featured = p.key === "creator";
            return (
              <div
                key={p.key}
                data-testid={`plan-card-${p.key}`}
                className={`rounded-2xl p-6 flex flex-col border transition-all ${
                  featured ? "bg-[#17131f] border-amber-500/40 amber-glow lg:scale-105" : "bg-[#141419] border-white/10"
                }`}
              >
                {featured && (
                  <div className="text-[10px] font-mono uppercase tracking-widest text-amber-400 mb-2 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Popular
                  </div>
                )}
                <h3 className="font-display text-xl font-bold">{p.name}</h3>
                <div className="mt-2 mb-1">
                  <span className="font-display text-3xl font-extrabold">£{p.price}</span>
                  {p.price > 0 && <span className="text-zinc-500 text-sm">/mo</span>}
                </div>
                <p className="text-xs text-zinc-500 mb-4">
                  {p.credits > 0 ? `${p.credits.toLocaleString()} credits / month` : "Discovery only"}
                </p>
                <ul className="space-y-2 mb-6 flex-1">
                  {(p.features || []).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs text-zinc-300">
                      <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => subscribe(p)}
                  disabled={isCurrent || busy === p.key}
                  data-testid={`subscribe-btn-${p.key}`}
                  className={`w-full font-bold ${
                    p.key === "free"
                      ? "bg-white/5 hover:bg-white/10 border border-white/10"
                      : "bg-amber-500 hover:bg-amber-400 text-zinc-950"
                  }`}
                >
                  {busy === p.key ? <Loader2 className="w-4 h-4 animate-spin" /> : isCurrent ? "Current plan" : p.key === "free" ? "Get Started" : "Subscribe"}
                </Button>
              </div>
            );
          })}
        </div>
        <div className="max-w-2xl mx-auto mt-10 text-center space-y-2" data-testid="billing-disclosure">
          <p className="text-zinc-400 text-sm">
            Paid plans are <span className="text-zinc-200 font-semibold">recurring monthly subscriptions</span> billed
            automatically until you cancel. Cancel anytime — your saved campaigns always stay free. See our{" "}
            <a href="/refunds" className="text-amber-400 hover:underline">Billing &amp; Refunds</a> and{" "}
            <a href="/terms" className="text-amber-400 hover:underline">Terms</a>.
          </p>
          <p className="text-zinc-600 text-xs">
            Test mode — use card 4242 4242 4242 4242, any future expiry &amp; CVC.
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
